import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  ChevronDown,
  ClipboardCheck,
  LogOut,
  Menu,
  MessageSquare,
  Search,
  ShieldCheck,
  UserCircle,
  X,
} from 'lucide-react';
import type { PortalRole, UserProfile } from '../types/auth';
import { resolveDynamicImageUrl } from '../lib/imageUrl';
import { useManproNotifications } from '../hooks/useManproNotifications';
import { useChannelList } from '../hooks/useChat';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';

type HeaderProps = {
  userRole: PortalRole;
  userProfile: UserProfile | null;
  onMenuClick: () => void;
  onLogout: () => void;
};

// Map notification types to icons
const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'evidence_submitted':
      return ClipboardCheck;
    case 'bid_submitted':
    case 'bid_accepted':
    case 'bid_rejected':
      return Briefcase;
    case 'task_approved':
    case 'task_rejected':
      return ShieldCheck;
    default:
      return Bell;
  }
};

const toTitleCase = (value: string, separatorPattern: RegExp) =>
  value
    .split(separatorPattern)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const Header = ({ userRole, userProfile, onMenuClick, onLogout }: HeaderProps) => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead, isLoading } = useManproNotifications();
  const { data: channels = [] } = useChannelList();

  const getAuthUserId = () => {
    try {
      const rawSession = localStorage.getItem('manproSessionProfile');
      const rawProto = localStorage.getItem('prototypeUserProfile');
      const raw = rawSession || rawProto;
      if (raw) return JSON.parse(raw)?.id;
    } catch {
      // ignore
    }
    return null;
  };

  const authUserId = getAuthUserId();

  const unreadChatCount = channels.reduce((acc, channel) => {
    const myChannelUser = channel.channel_users?.find(
      (cu) => String(cu.user_id) === String(authUserId)
    );
    if (myChannelUser && myChannelUser.is_read === 0) return acc + 1;
    if (!myChannelUser && channel.is_read === 0) return acc + 1;
    return acc;
  }, 0);

  const displayName = userProfile?.first_name
    ? `${userProfile.first_name} ${userProfile.last_name ?? ''}`.trim()
    : userProfile?.name || 'Provider Indoteknisi';

  const displayEmail = userProfile?.email || 'provider@indoteknisi.id';

  const displayRole = userProfile?.roles?.[0]?.role_name
    ? toTitleCase(userProfile.roles[0].role_name.replace(/_/g, ' '), / /)
    : userProfile?.user_type
      ? toTitleCase(userProfile.user_type, /-/)
      : 'Provider';


  return (
    <>
      <header className="sticky top-0 z-30 h-[76px] border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="flex h-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={onMenuClick}
            className="rounded-xl border border-slate-200 p-2 text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 lg:hidden"
            aria-label="Buka menu"
          >
            <Menu size={20} />
          </button>

          <label className="hidden w-full max-w-md items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-500 transition focus-within:border-blue-300 focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-100 md:flex">
            <Search size={17} />
            <input
              placeholder="Cari proyek, teknisi, task, atau vendor..."
              className="w-full bg-transparent text-slate-700 outline-none placeholder:text-slate-400"
            />
          </label>

          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => setIsNotificationOpen(true)}
              className="relative rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 cursor-pointer"
              aria-label="Notifikasi"
              type="button"
            >
              <Bell size={21} />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-extrabold text-white ring-2 ring-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            <Link
              to="/provider-pesan"
              className="relative rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 cursor-pointer"
              aria-label="Pesan"
            >
              <MessageSquare size={21} />
              {unreadChatCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-extrabold text-white ring-2 ring-white">
                  {unreadChatCount > 99 ? '99+' : unreadChatCount}
                </span>
              )}
            </Link>

            <div className="flex items-center gap-3 pl-2 border-l border-slate-200">
              <div className="flex items-center gap-3 rounded-2xl px-2 py-1.5">
                {userProfile?.profile_image_full_url || userProfile?.profile_image_full_path ? (
                  <img
                    src={resolveDynamicImageUrl(userProfile.profile_image_full_url || userProfile.profile_image_full_path)}
                    alt="Profile"
                    className="h-10 w-10 rounded-full object-cover ring-2 ring-slate-100"
                  />
                ) : (
                  <UserCircle size={40} className="text-blue-600" />
                )}
                <div className="hidden text-left md:block min-w-[120px] max-w-[200px]">
                  <p className="text-sm font-extrabold leading-tight text-slate-800 truncate">{displayName}</p>
                  <p className="mt-0.5 text-xs font-medium text-slate-500 truncate">{displayEmail}</p>
                </div>
              </div>

              <button
                onClick={onLogout}
                className="flex items-center justify-center rounded-xl p-2.5 text-red-500 transition hover:bg-red-50 hover:text-red-600 cursor-pointer"
                aria-label="Keluar"
                title="Keluar"
                type="button"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {isNotificationOpen && (
        <button
          type="button"
          aria-label="Tutup notifikasi"
          onClick={() => setIsNotificationOpen(false)}
          className="fixed inset-0 z-40 bg-slate-950/30 backdrop-blur-[2px]"
        />
      )}

      <aside
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-[420px] transform border-l border-slate-200 bg-white shadow-2xl shadow-slate-900/20 transition-transform duration-300 ease-out ${
          isNotificationOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between border-b border-slate-100 p-5">
            <div>
              <h2 className="text-xl font-extrabold text-slate-900">Notifikasi</h2>
              <p className="mt-1 text-sm text-slate-500">Update terbaru dari operational marketplace.</p>
            </div>
            <button
              type="button"
              onClick={() => setIsNotificationOpen(false)}
              className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Tutup panel notifikasi"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 p-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                  <Bell size={32} />
                </div>
                <h3 className="text-lg font-bold text-slate-800">Belum ada notifikasi</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Anda akan mendapatkan update aktivitas di sini.
                </p>
              </div>
            ) : (
              notifications.map((item) => {
                const Icon = getNotificationIcon(item.type);
                const timeString = formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: id });

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (!item.is_read) markAsRead(item.id);
                    }}
                    className={`w-full text-left transition ${
                      item.is_read ? 'opacity-70' : ''
                    }`}
                  >
                    <div className={`rounded-2xl border bg-white p-4 shadow-sm transition hover:shadow-md ${
                      !item.is_read ? 'border-blue-200 ring-1 ring-blue-50' : 'border-slate-200'
                    }`}>
                      <div className="flex gap-3">
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                          !item.is_read ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                        }`}>
                          <Icon size={19} />
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <p className={`truncate font-extrabold ${!item.is_read ? 'text-slate-900' : 'text-slate-700'}`}>
                            {item.title}
                          </p>
                          <p className="mt-1 text-sm leading-6 text-slate-500 line-clamp-2">
                            {item.message}
                          </p>
                          <p className="mt-2 text-xs font-semibold text-slate-400">
                            {timeString}
                          </p>
                        </div>
                        {!item.is_read && (
                          <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-600"></div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="border-t border-slate-100 p-4 flex flex-col gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllAsRead()}
                  className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-blue-700 cursor-pointer"
                >
                  Tandai Semua Dibaca
                </button>
              )}
              <Link
                to="/notifikasi"
                onClick={() => setIsNotificationOpen(false)}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-center text-sm font-extrabold text-slate-700 transition hover:bg-slate-50 hover:text-blue-600"
              >
                Lihat Semua Notifikasi
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Header;
